import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  FaArrowRight,
  FaChartLine,
  FaExclamationTriangle,
  FaLightbulb,
  FaRobot,
  FaSyncAlt,
  FaUtensils,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { API_BASE_URL, API_ENDPOINTS } from "../config/constants";
import { getOfflineSmartInsights } from "../offline/financeStore";
import { formatStoredMoney } from "../utils/currency";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";

const ymd = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const rangeForDays = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days + 1);
  const previousEnd = new Date(start);
  previousEnd.setDate(previousEnd.getDate() - 1);
  const previousStart = new Date(previousEnd);
  previousStart.setDate(previousStart.getDate() - days + 1);
  return {
    from: ymd(start),
    to: ymd(end),
    previousFrom: ymd(previousStart),
    previousTo: ymd(previousEnd),
  };
};

const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || "Unable to analyse business data");
  }
  return payload;
};

const normalizeProducts = (payload) =>
  (Array.isArray(payload?.items) ? payload.items : []).map((item) => ({
    name: item?.name || "Unnamed product",
    quantity: Number(item?.total_quantity || 0),
    orders: Number(item?.total_orders || 0),
    revenue: Number(item?.gross_amount || 0),
  }));

const median = (values) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const SmartInsightsPage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [unavailableSources, setUnavailableSources] = useState([]);
  const [expandedSegments, setExpandedSegments] = useState({});
  const [data, setData] = useState({
    products: [],
    previousProducts: [],
    categories: [],
  });

  const loadInsights = useCallback(async () => {
    const profile = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!profile?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const range = rangeForDays(days);
    // The existing top-products endpoint accepts a maximum of 50 rows.
    const base = { restaurantid: profile.id, limit: 50 };
    try {
      if (isFullOfflineModeEnabled()) {
        const offline = await getOfflineSmartInsights(profile.id, {
          currentFrom: range.from,
          currentTo: range.to,
          previousFrom: range.previousFrom,
          previousTo: range.previousTo,
        });
        setData({
          products: normalizeProducts(offline?.current_products),
          previousProducts: normalizeProducts(offline?.previous_products),
          categories: Array.isArray(offline?.categories?.items)
            ? offline.categories.items : [],
        });
        setUnavailableSources([]);
        setUpdatedAt(new Date());
        return;
      }
      const results = await Promise.allSettled([
        postJson(API_ENDPOINTS.REPORT_TOP_PRODUCTS, {
          ...base,
          fromdate: range.from,
          todate: range.to,
        }),
        postJson(API_ENDPOINTS.REPORT_TOP_PRODUCTS, {
          ...base,
          fromdate: range.previousFrom,
          todate: range.previousTo,
        }),
        postJson(`${API_BASE_URL}/report/category-performance`, {
          restaurantid: profile.id,
          fromdate: range.from,
          todate: range.to,
        }),
      ]);

      const sourceNames = [
        "current sales",
        "previous-period sales",
        "category performance",
      ];
      const failedSources = results
        .map((result, index) => result.status === "rejected" ? sourceNames[index] : null)
        .filter(Boolean);
      const valueAt = (index) =>
        results[index]?.status === "fulfilled" ? results[index].value : {};
      const current = valueAt(0);
      const previous = valueAt(1);
      const categories = valueAt(2);

      setData({
        products: normalizeProducts(current),
        previousProducts: normalizeProducts(previous),
        categories: Array.isArray(categories?.items) ? categories.items : [],
      });
      setUnavailableSources(failedSources);
      setUpdatedAt(new Date());
      if (failedSources.length === results.length) {
        const firstError = results.find((result) => result.status === "rejected")?.reason;
        toast.error(firstError?.message || "Business data is currently unavailable");
      } else if (failedSources.length) {
        toast.warning(`Some insights are unavailable: ${failedSources.join(", ")}.`);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to generate insights");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const analysis = useMemo(() => {
    const total = (rows, key) => rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    const revenue = total(data.products, "revenue");
    const previousRevenue = total(data.previousProducts, "revenue");
    const quantity = total(data.products, "quantity");
    const previousQuantity = total(data.previousProducts, "quantity");
    const revenueChange = previousRevenue
      ? ((revenue - previousRevenue) / previousRevenue) * 100
      : null;
    const quantityChange = previousQuantity
      ? ((quantity - previousQuantity) / previousQuantity) * 100
      : null;
    const topProduct = data.products[0] || null;
    const topThreeRevenue = data.products
      .slice(0, 3)
      .reduce((sum, item) => sum + item.revenue, 0);
    const topThreeShare = revenue ? (topThreeRevenue / revenue) * 100 : 0;
    const topCategory = [...data.categories].sort(
      (a, b) => Number(b?.gross_amount || 0) - Number(a?.gross_amount || 0)
    )[0];
    const quantityMedian = median(data.products.map((item) => item.quantity));
    const revenueMedian = median(data.products.map((item) => item.revenue));
    const classified = data.products.map((item) => {
      const highDemand = item.quantity >= quantityMedian;
      const highRevenue = item.revenue >= revenueMedian;
      const segment = highDemand && highRevenue
        ? "Stars"
        : highDemand
          ? "Volume Drivers"
          : highRevenue
            ? "Premium Opportunities"
            : "Needs Attention";
      return { ...item, segment };
    });

    const insights = [];
    if (revenueChange !== null) {
      insights.push({
        tone: revenueChange >= 0 ? "positive" : "warning",
        icon: <FaChartLine />,
        title: `Revenue is ${Math.abs(revenueChange).toFixed(1)}% ${revenueChange >= 0 ? "higher" : "lower"}`,
        text: `Compared with the previous ${days}-day period. ${revenueChange < 0 ? "Review low-performing products and recent demand changes." : "Keep the strongest products visible and consistently available."}`,
        route: "/sales-report",
        action: "Open sales report",
      });
    }
    if (topProduct) {
      insights.push({
        tone: "info",
        icon: <FaUtensils />,
        title: `${topProduct.name} leads demand`,
        text: `${topProduct.quantity} units generated ${formatStoredMoney(topProduct.revenue)} in the selected period.`,
        route: "/reports/top-products",
        action: "View top products",
      });
    }
    if (topCategory) {
      insights.push({
        tone: "info",
        icon: <FaLightbulb />,
        title: `${topCategory.category_name || "Top category"} is the strongest category`,
        text: `It contributed ${Number(topCategory.contribution_percent || 0).toFixed(1)}% of reported category revenue.`,
        route: "/reports/category-performance",
        action: "View categories",
      });
    }
    if (data.products.length >= 3) {
      insights.push({
        tone: topThreeShare >= 60 ? "warning" : "positive",
        icon: <FaChartLine />,
        title: `Top 3 products contribute ${topThreeShare.toFixed(1)}% of analysed revenue`,
        text: topThreeShare >= 60
          ? "Sales are concentrated in a few products. Keep them prominent and build demand for other menu items."
          : "Revenue is distributed across the menu, reducing dependence on only a few products.",
        route: "/reports/top-products",
        action: "Review product performance",
      });
    }

    return { revenue, quantity, revenueChange, quantityChange, classified, insights };
  }, [data, days]);

  const segments = [
    { name: "Stars", description: "High demand and high revenue", tone: "green" },
    { name: "Volume Drivers", description: "High demand with lower revenue", tone: "blue" },
    { name: "Premium Opportunities", description: "Higher revenue with lower demand", tone: "purple" },
    { name: "Needs Attention", description: "Lower demand and lower revenue", tone: "orange" },
  ];

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2400} />
      <Hero>
        <HeroCopy>
          <HeroBadge><FaRobot /> Smart insights</HeroBadge>
          <h1>Business Assistant &amp; Menu Intelligence</h1>
          <p>Frontend-generated recommendations based on your existing sales, menu and inventory data.</p>
        </HeroCopy>
        <Controls>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button type="button" onClick={loadInsights} disabled={loading}>
            <FaSyncAlt className={loading ? "spin" : ""} /> {loading ? "Analysing..." : "Refresh insights"}
          </button>
        </Controls>
      </Hero>

      {unavailableSources.length ? (
        <PartialNotice>
          <FaExclamationTriangle /> Showing available insights. Could not load: {unavailableSources.join(", ")}.
        </PartialNotice>
      ) : null}

      <Stats>
        <Stat><span>Analysed revenue</span><strong>{formatStoredMoney(analysis.revenue)}</strong><small>{analysis.revenueChange === null ? "No previous-period baseline" : `${analysis.revenueChange >= 0 ? "+" : ""}${analysis.revenueChange.toFixed(1)}% vs previous period`}</small></Stat>
        <Stat><span>Items sold</span><strong>{analysis.quantity.toLocaleString("en-IN")}</strong><small>{analysis.quantityChange === null ? "No previous-period baseline" : `${analysis.quantityChange >= 0 ? "+" : ""}${analysis.quantityChange.toFixed(1)}% vs previous period`}</small></Stat>
        <Stat><span>Products analysed</span><strong>{analysis.classified.length}</strong><small>Based on available sales records</small></Stat>
        <Stat><span>Average per product</span><strong>{formatStoredMoney(analysis.classified.length ? analysis.revenue / analysis.classified.length : 0)}</strong><small>Average analysed product revenue</small></Stat>
      </Stats>

      <SectionHead>
        <div><span>SMART BUSINESS ASSISTANT</span><h2>Recommended actions</h2></div>
        {updatedAt ? <small>Updated {updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small> : null}
      </SectionHead>
      {loading ? <Empty>Analysing your business data...</Empty> : analysis.insights.length ? (
        <InsightGrid>
          {analysis.insights.map((insight, index) => (
            <Insight key={`${insight.title}-${index}`} $tone={insight.tone}>
              <InsightIcon>{insight.icon}</InsightIcon>
              <div><h3>{insight.title}</h3><p>{insight.text}</p><button type="button" onClick={() => navigate(insight.route)}>{insight.action} <FaArrowRight /></button></div>
            </Insight>
          ))}
        </InsightGrid>
      ) : <Empty>No sales insights are available for this period yet.</Empty>}

      <SectionHead>
        <div><span>MENU INTELLIGENCE</span><h2>Product performance map</h2></div>
        <small>Relative to the median of products in this period</small>
      </SectionHead>
      <SegmentGrid>
        {segments.map((segment) => {
          const products = analysis.classified.filter((item) => item.segment === segment.name);
          const expanded = Boolean(expandedSegments[segment.name]);
          const visibleProducts = expanded ? products : products.slice(0, 6);
          return (
            <Segment key={segment.name} $tone={segment.tone}>
              <SegmentTop><div><span>{segment.name}</span><small>{segment.description}</small></div><b>{products.length}</b></SegmentTop>
              <ProductList>
                {visibleProducts.map((product) => (
                  <Product key={`${segment.name}-${product.name}`}>
                    <div><strong>{product.name}</strong><small>{product.quantity} sold · {product.orders} orders</small></div>
                    <b>{formatStoredMoney(product.revenue)}</b>
                  </Product>
                ))}
                {!products.length ? <NoProducts>No products in this group</NoProducts> : null}
              </ProductList>
              {products.length > 6 ? (
                <MoreCount
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedSegments((current) => ({
                    ...current,
                    [segment.name]: !expanded,
                  }))}
                >
                  {expanded ? "Show fewer products" : `+${products.length - 6} more products`}
                </MoreCount>
              ) : null}
            </Segment>
          );
        })}
      </SegmentGrid>
    </Page>
  );
};

export default SmartInsightsPage;

const Page = styled.main`min-height:100vh;background:#f4f7fb;padding:20px;color:#0f172a;`;
const Hero = styled.section`display:flex;justify-content:space-between;gap:20px;align-items:flex-end;padding:26px;border-radius:22px;background:linear-gradient(130deg,#063b63,var(--app-primary) 55%,var(--app-primary-hover));color:#fff;box-shadow:0 16px 40px rgba(var(--app-primary-rgb), .18);@media(max-width:760px){align-items:stretch;flex-direction:column;}`;
const HeroCopy = styled.div`h1{margin:8px 0 6px;font-size:clamp(1.5rem,3vw,2.25rem)}p{margin:0;color:#d8edfb;max-width:720px}`;
const HeroBadge = styled.span`display:inline-flex;align-items:center;gap:7px;font-size:.75rem;font-weight:900;letter-spacing:.09em;text-transform:uppercase;`;
const Controls = styled.div`display:flex;gap:9px;flex-wrap:wrap;select,button{border:1px solid rgba(255,255,255,.3);border-radius:11px;padding:11px 13px;font-weight:800}select{background:#fff;color:#17324a}button{display:flex;align-items:center;gap:7px;background:#082f49;color:#fff;cursor:pointer}button:disabled{opacity:.7}.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`;
const PartialNotice = styled.div`display:flex;align-items:center;gap:9px;margin:14px 0;padding:11px 14px;border:1px solid #fde68a;background:#fffbeb;color:#92400e;border-radius:12px;font-size:.82rem;font-weight:700;`;
const Stats = styled.section`display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:16px 0 26px;@media(max-width:900px){grid-template-columns:repeat(2,1fr)}@media(max-width:480px){grid-template-columns:1fr}`;
const Stat = styled.article`background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:16px;box-shadow:0 5px 18px rgba(15,23,42,.04);span,small{display:block;color:#64748b}span{font-size:.76rem;font-weight:850;text-transform:uppercase;letter-spacing:.05em}strong{display:block;margin:7px 0 4px;font-size:1.55rem}small{font-size:.76rem}`;
const SectionHead = styled.div`display:flex;justify-content:space-between;align-items:end;gap:12px;margin:24px 2px 12px;span{font-size:.68rem;color:var(--app-primary);font-weight:900;letter-spacing:.12em}h2{margin:3px 0 0;font-size:1.2rem}small{color:#64748b;text-align:right}`;
const InsightGrid = styled.section`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;@media(max-width:760px){grid-template-columns:1fr}`;
const Insight = styled.article`display:flex;gap:13px;background:#fff;border:1px solid ${({$tone})=>$tone === "danger" ? "#fecaca" : $tone === "warning" ? "#fde68a" : "var(--app-primary-soft)"};border-radius:16px;padding:16px;h3{margin:0 0 5px;font-size:1rem}p{margin:0;color:#64748b;font-size:.85rem;line-height:1.5}button{display:flex;align-items:center;gap:6px;margin-top:11px;padding:0;border:0;background:none;color:var(--app-primary);font-weight:850;cursor:pointer}`;
const InsightIcon = styled.div`flex:0 0 38px;height:38px;display:grid;place-items:center;border-radius:12px;background:var(--app-primary-soft);color:var(--app-primary);`;
const SegmentGrid = styled.section`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:13px;@media(max-width:820px){grid-template-columns:1fr}`;
const Segment = styled.article`background:#fff;border:1px solid #e2e8f0;border-top:4px solid ${({$tone})=>({green:"#16a34a",blue:"var(--app-primary)",purple:"#7c3aed",orange:"#ea580c"}[$tone])};border-radius:16px;padding:15px;box-shadow:0 5px 18px rgba(15,23,42,.04);`;
const SegmentTop = styled.div`display:flex;justify-content:space-between;align-items:center;padding-bottom:11px;border-bottom:1px solid #edf2f7;span,small{display:block}span{font-weight:900}small{margin-top:3px;color:#64748b}b{display:grid;place-items:center;width:34px;height:34px;border-radius:50%;background:#f1f5f9}`;
const ProductList = styled.div``;
const Product = styled.div`display:flex;justify-content:space-between;gap:10px;align-items:center;padding:11px 2px;border-bottom:1px solid #f1f5f9;div strong,div small{display:block}div small{margin-top:3px;color:#64748b;font-size:.75rem}>b{font-size:.86rem;white-space:nowrap}`;
const NoProducts = styled.div`padding:22px 4px;text-align:center;color:#94a3b8;font-size:.85rem;`;
const MoreCount = styled.button`display:block;width:100%;margin-top:8px;padding:9px 10px;border:0;border-radius:9px;background:#f1f5f9;color:var(--app-primary);font-size:.78rem;font-weight:850;text-align:center;cursor:pointer;transition:.15s ease;&:hover{background:var(--app-primary-soft);color:var(--app-primary)}&:focus-visible{outline:3px solid rgba(2,132,199,.25);outline-offset:2px}`;
const Empty = styled.div`padding:34px;text-align:center;background:#fff;border:1px dashed #cbd5e1;border-radius:16px;color:#64748b;`;
